import React, { useEffect, useState } from "react";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { useLocation, useNavigate } from "react-router-dom";
import { ScanData } from "../../../repositories/WalletRepository";
import SendTxStep1 from "./components/SendTxStep1";
import SendTxStep2 from "./components/SendTxStep2";
import SendTxStep3 from "./components/SendTxStep3";
import { useTheme } from "../../../hooks/useTheme";
import { Transaction } from "../../../models/Transactions";
import { Pleb } from "../../../models/Pleb";
import { BulkPayment } from "../../../models/BulkPayment";

export interface SendTransactionStateRecipient {
    lnurl?: string;
    invoice?: string;
    pleb?: Pleb | null;
    bulkPayment?: BulkPayment | null;
}

export interface SendTransactionState {
    recipient?: SendTransactionStateRecipient;
    scanData?: ScanData | null;
    amount?: number;
    comment?: string;
    transaction?: Transaction;
    anonymous?: boolean;
}

interface SendTransactionProps {
  
}

const getDefaultStateFromLocation = (location: any) => {
    if(location.state && location.state.recipient) {
        if(location.state.recipient.pleb) {
            location.state.recipient.pleb = new Pleb(location.state.recipient.pleb);
        }
        if(location.state.recipient.bulkPayment) {
            location.state.recipient.bulkPayment = new BulkPayment(location.state.recipient.bulkPayment);
        }
        return {recipient: location.state.recipient}
    }
    return {}
}

const SendTransaction: React.FC<SendTransactionProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [state, setState] = useState<SendTransactionState>(getDefaultStateFromLocation(location));
    const {isMobile} = useTheme();
    const [recipientAutoPost, setRecipientAutoPost] = useState<SendTransactionStateRecipient | null>(null);

    useEffect(() => {
        if(location.state?.timestamp) {
            setStep(1);
            setState(getDefaultStateFromLocation(location));
        }
    }, [location.state?.timestamp]);

    useEffect(() => {
        if(location.state && location.state.recipient) {
            setRecipientAutoPost(location.state.recipient);
        }
    }, []);

    const onConfirmStep1 = (data: ScanData | null, recipient: SendTransactionStateRecipient) => {
        setRecipientAutoPost(null);
        setState({
            ...state, 
            scanData: data, 
            amount: data?.fixedAmountMsat ? data?.fixedAmountMsat / 1000 : undefined, 
            recipient: recipient
        });

        if(recipient.invoice) {
            setStep(3);
        }
        else {
            setStep(2);
        }
    }

    const onConfirmStep2 = (amount: number) => {
        setState({...state, amount: amount});
        setStep(3);
    }

    const onClickPrevArrow = () => {
        let newStep: number | null = null;

        if(isMobile)
        {
            if(step > 1) {
                newStep = step - 1;
            } else {
                return () => navigate('/home/wallet');
            }
        }
        else if(step > 1) {
            newStep = step - 1;
        }

        if(newStep === 2 && state.recipient?.invoice) {
            newStep = 1;
        }

        if(newStep) {
            return () => setStep(newStep || 1);
        }
    }
    
    return (
        <LayoutWithTopBar
            breadcrumbs={[
                { title: 'Wallet', onClick: () => navigate('/home/wallet') },
                { title: 'Send' }
            ]}
            onClickPrevArrow={onClickPrevArrow()}
        >
            {
                step === 1 && <SendTxStep1 
                    defaultRecipient={state.recipient ?? recipientAutoPost ?? undefined}
                    recipientAutoPost={recipientAutoPost}
                    onScanRecipient={(data) => {
                        if(data.recipient)
                            onConfirmStep1(data.scanData ?? null, data.recipient);
                    }}
                />
            }
            {
                step === 2 && <SendTxStep2 
                    onConfirm={onConfirmStep2}
                    defaultAmount={state.amount ?? undefined}
                    recipient={state.recipient ?? undefined}
                    scanData={state?.scanData ?? undefined}
                />
            }
            {
                step === 3 && <SendTxStep3 
                    transactionState={state}
                    onChangeComment={(comment: string) => setState({...state, comment: comment})}
                    onValidateTransaction={(transaction: Transaction) => {
                        navigate('/home/wallet/tx/' + transaction.paymentHash, { state: { template: 'success' } });
                    }}
                    onChangeAnonymously={(anonymous: boolean) => setState({...state, anonymous: anonymous})}
                />
            }
        </LayoutWithTopBar>
    );
}

export default SendTransaction;

export const getNameToShow = (recipient?: SendTransactionStateRecipient): string => {
    if(recipient?.pleb) {
        return recipient.pleb.getLabelToShowSendToWallet();
    }
    else if(recipient?.lnurl) {
        return recipient.lnurl;
    }
    else if(recipient?.invoice) {
        return recipient.invoice;
    }
    return '';
}